// The Studio page: open the room, find out who is here, then hand the core,
// the overlay and the presenter to each other.

import { Effect } from "effect";
import { AccountController, accountTransport } from "../account/client.ts";
import type { Plan } from "../account/contract.ts";
import { JOIN_PARAM, openAgreement, openOffer, openSignIn } from "../ui/account.ts";
import { MakeView } from "../ui/make.ts";
import { OFFER_ID } from "../core/offers.ts";
import { MEMBERSHIP_SIGNUPS_OPEN } from "../core/membership.ts";
import { arrived, enableEvents, track, watch } from "../api/events.ts";
import type { AgreementResponse } from "../core/legal.ts";
import { Stage } from "../stage/stage.ts";
import { Refused, StudioCore, type StudioBackend } from "../core/studio.ts";
import { httpBackend, providers, signInAsGuest, whoAmI } from "../api/http.ts";
import type { AuthProvidersResponse } from "../core/protocol.ts";
import { localBackend } from "../api/local.ts";
import { forget, recall } from "../core/wall.ts";
import { faceReady } from "../stage/type.ts";
import { Overlay } from "../ui/overlay.ts";
import { lang, langFetch, t } from "../ui/lang.ts";
import { StudioView } from "./view.ts";

// The room's language, read before anything else reads the address: a `?lang=` is saved and taken out of it.
lang();
const loading = document.querySelector(".loading");
if (loading) loading.textContent = t("room.loading");

// The account over the same typed contract the simulation tests drive.
const account = new AccountController(accountTransport(location.origin, langFetch));

const stage = new Stage(document.querySelector<HTMLCanvasElement>("#stage")!, { dracoPath: "/vendor/draco/" });
let core: StudioCore | null = null;
let view: StudioView | null = null;

const overlay = new Overlay(document.querySelector<HTMLElement>("#ui")!, () => core, {
  selectDevice: (device) => core?.selectDevice(device),
  selectApp: (app) => core?.selectApp(app),
  openLaptop: () => core?.openLaptop(),
  turnShelf: (by) => view?.turnShelf(by),
  openCloset: () => core?.openCloset(),
  openSettings: () => view?.openSettings(),
  openTv: () => core?.openTv(),
  tvTurn: (by) => view?.tvTurn(by),
  tvChoose: () => view?.tvChoose(),
  tvMuted: () => view?.tvMuted ?? false,
  tvMute: (muted) => view?.setTvMuted(muted),
  native: (app, device) => void view?.native(app, device),
  account: {
    state: () => Effect.runPromise(account.refresh()),
    providers,
    checkout: async (plan, place) => (await Effect.runPromise(account.checkout(plan, place))).url,
    portal: async () => (await Effect.runPromise(account.portal())).url,
    setHandle: async (handle) => {
      if (!core) return { error: t("room.notOpen") };
      try {
        await core.setHandle(handle);
        return { handle: core.state.snapshot.user.handle };
      } catch (error) {
        return { error: error instanceof Refused ? error.message : t("account.handle.failed") };
      }
    },
    signOut: () => void view?.signOut(),
    toast: (message) => overlay.toast(message),
  },
  back: () => core?.back(),
  play: (app, device) => view?.play(app, device),
  retitle: async (app, title) => {
    try {
      await core?.setTitle(app, title);
      return null;
    } catch (error) {
      return error instanceof Refused ? error.message : t("room.rename.failed");
    }
  },
  skin: (device, skin, color) => void view?.setSkin(device, skin, color),
  previewColor: (device, color) => view?.previewColor(device, color),
  publish: (app, on) => void core?.setPublished(app, on).catch(() => overlay.toast(t("room.changeFailed"))),
  allowNative: (app, on) => void core?.setNativeAllowed(app, on).catch(() => overlay.toast(t("room.changeFailed"))),
  keep: (app, on) => {
    // The game the room's link names came from that link; any other game off the shelf was found on Pocket TV.
    const via = new URLSearchParams(location.search).get("app") === app ? "link" : "tv";
    void core?.setKept(app, on, via).then(
      () => overlay.toast(t(on ? "room.kept" : "room.unkept")),
      (error) => overlay.toast(error instanceof Refused ? error.message : t("room.changeFailed")),
    );
  },
  rename: async (app, slug) => {
    try {
      await core?.setSlug(app, slug);
      return null;
    } catch (error) {
      return error instanceof Refused ? error.message : t("room.address.failed");
    }
  },
  remove: (app) =>
    void core?.deleteApp(app).catch((error) => {
      overlay.toast(error instanceof Refused ? error.message : t("room.delete.failed"));
      // The card asks again from its first button.
      if (core) overlay.render(core.state);
    }),
  signOut: () => void view?.signOut(),
});

// Making an app for the Tab5 in the room (docs/make.md): shown once the server says the maker is open to this browser.
const make = new MakeView(document.querySelector<HTMLElement>("#ui")!, {
  core: () => core,
  toast: (message) => overlay.toast(message),
  signIn: () => openSignIn(document.querySelector<HTMLElement>("#ui")!, overlay.actions.account, t("wall.make"), () => location.reload(), "create"),
});

/** Hold the room behind a sign-in card until there is a session. */
async function gate(): Promise<void> {
  const available = await providers().catch((): AuthProvidersResponse => ({ github: false, guest: true }));
  await new Promise<void>((resolve) => {
    const card = overlay.signIn({
      providers: available,
      onGuest: async () => {
        await signInAsGuest();
        card.remove();
        resolve();
      },
      onDone: () => {
        card.remove();
        resolve();
      },
    });
  });
}

async function boot(): Promise<void> {
  const room = stage.setRoom("/rooms/workshop/");
  const who = await whoAmI();
  await room;
  stage.start();
  document.documentElement.dataset.ready = "room";

  let backend: StudioBackend;
  if (who === "offline") {
    // The offline preview: ?member wears a membership, ?games=N puts N more games on the rack.
    const preview = new URLSearchParams(location.search);
    backend = await localBackend("/apps/", preview.has("member"), Math.max(0, Math.floor(Number(preview.get("games")) || 0)));
  } else {
    // A visitor comes in as a guest, with no card in the way: the room asks
    // for an account when something needs one (core/wall.ts). The landing
    // page's "Sign in" asks for the card by name (`?signin`), and a browser
    // the server gives no guest session meets it too.
    // This page has a server: what it reports from here on is sent.
    enableEvents();
    if (who === null) {
      const asked = new URLSearchParams(location.search).has("signin");
      if (asked || !(await signInAsGuest().then(() => true, () => false))) await gate();
      if (asked) {
        const url = new URL(location.href);
        url.searchParams.delete("signin");
        history.replaceState(null, "", url);
      }
    }
    backend = httpBackend;
  }
  // Device screens and game covers are drawn on canvases with the page's
  // fonts, so those must be loaded first. A slow font host does not hold the room.
  const [snapshot] = await Promise.all([
    backend.snapshot(),
    Promise.race([
      faceReady(),
      new Promise((resolve) => setTimeout(resolve, 2500)),
    ]),
  ]);
  core = new StudioCore(backend, snapshot);
  overlay.setUser(snapshot.user);
  view = new StudioView(stage, core, overlay, { devicesUrl: "/devices/", runtimeUrl: "/runtime/", desktopUrl: "/desktop/", tvUrl: "/tv/" });
  await view.init();
  // A share link opens on its game's card.
  const query = new URLSearchParams(location.search);
  const sharedApp = query.get("app");
  if (sharedApp && core.app(sharedApp)) core.selectApp(sharedApp);
  document.documentElement.dataset.ready = "true";
  track("room.opened", { props: { as: who === "offline" ? "none" : snapshot.user.guest ? "guest" : "account" } });
  arrived(sharedApp && core.app(sharedApp) ? sharedApp : null);
  watch(core);
  if (who !== "offline" && (await make.start())) {
    core.subscribe((state) => make.render(state));
    make.render(core.state);
    // A message written before a sign-in took the visitor away is sent now.
    const prompt = make.takePrompt();
    if (prompt) void make.begin(prompt);
  }
  afterReturn(query);
  if (who !== "offline" && !snapshot.user.guest) void agreement();

  // What is on the shelf changes on the server, so the snapshot is polled now and then.
  let last = 0;
  setInterval(() => {
    if (!core || document.hidden) return;
    if (performance.now() - last < 20000) return;
    last = performance.now();
    core.refresh().catch(() => null);
  }, 500);

  if (/^(localhost|127\.0\.0\.1)$/.test(location.hostname)) {
    (globalThis as Record<string, unknown>).__studio = { stage, core, view };
  }
}

/** An account that has not agreed to the current Terms and Privacy Policy is asked once. */
async function agreement(): Promise<void> {
  const response = await fetch("/api/agreement", { credentials: "same-origin" }).catch(() => null);
  if (!response?.ok) return;
  if (((await response.json()) as AgreementResponse).current) return;
  openAgreement(
    document.querySelector<HTMLElement>("#ui")!,
    async () => {
      const saved = await fetch("/api/agreement", { method: "POST", credentials: "same-origin" });
      if (!saved.ok) throw new Error("not saved");
    },
    () => void view?.signOut(),
  );
}

/**
 * What a return to the room carries in its address: the outcome of a payment,
 * or a plan chosen before a sign-in that left the page. Each is acted on once
 * and taken out of the address.
 */
function afterReturn(query: URLSearchParams): void {
  // What a guest reached for before the sign-in card: it starts now, for an account.
  const wanted = recall(query);
  if (wanted) {
    history.replaceState(null, "", forget(new URL(location.href)));
    if (core && !core.state.snapshot.user.guest) view?.resume(wanted);
  }
  // An offer's own address (src/core/offers.ts): the page that claims it, for an account or a guest asked to sign in.
  const offer = query.get("offer");
  if (MEMBERSHIP_SIGNUPS_OPEN && offer && OFFER_ID.test(offer)) openOffer(document.querySelector<HTMLElement>("#ui")!, overlay.actions.account, offer, () => void core?.refresh().catch(() => null));
  const paid = query.get("checkout");
  const join = query.get(JOIN_PARAM);
  if (!paid && !join) return;
  const url = new URL(location.href);
  url.searchParams.delete("checkout");
  url.searchParams.delete(JOIN_PARAM);
  history.replaceState(null, "", url);
  if (!core) return;
  if (paid === "success") {
    // The payment provider tells the server, not this page. Look until the
    // membership shows, for half a minute.
    overlay.toast(t("checkout.success"));
    let tries = 0;
    const timer = setInterval(() => {
      if (!core || core.state.snapshot.membership.active || ++tries > 15) {
        clearInterval(timer);
        if (core?.state.snapshot.membership.active) overlay.toast(t("checkout.member"));
        return;
      }
      core.refresh().catch(() => null);
    }, 2000);
  } else if (paid === "cancelled") {
    overlay.toast(t("checkout.cancelled"));
  } else if (MEMBERSHIP_SIGNUPS_OPEN && (join === "month" || join === "year") && !core.state.snapshot.membership.active) {
    overlay.paywall("member", join as Plan);
  }
}

boot().catch((error) => {
  console.error(error);
  document.documentElement.dataset.error = String(error?.message ?? error);
});
